import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getAppOnlineStatus, setError, setLoading, setMembers } from '../../../slice';
import { fetchChannelMembers } from '../channelInfoUtils';

export function useChannelMembersList(
    channelId,
    { pageSize = 20, initialSearch = '', debounceMs = 200, membersCount } = {},
) {
    const dispatch = useDispatch();

    const channelState = useSelector(state => state.channelMembersData.membersByChannel[channelId], shallowEqual);
    const { members: allMembers = [], total = 0, loading = false, error = null } = channelState || {};

    const [visibleCount, setVisibleCount] = useState(pageSize);
    const [searchText, setSearchText] = useState(initialSearch);
    const [currentPage, setCurrentPage] = useState(1);
    const [canFetchMore, setCanFetchMore] = useState(true);

    const debounceTimer = useRef(null);
    const lastFetchedParams = useRef({ search: '', page: 0 });

    const fetchMembers = useCallback(
        async (search = '', page = 1, force = false) => {
            if (!getAppOnlineStatus()) return;
            if (!force && lastFetchedParams.current.search === search && lastFetchedParams.current.page === page)
                return;

            lastFetchedParams.current = { search, page };

            dispatch(setLoading({ channelId, loading: true }));
            try {
                const { members, totalMembers } = await fetchChannelMembers({
                    channelId,
                    page,
                    size: pageSize,
                    search,
                });

                // if API returned less than a full page => stop future fetches
                setCanFetchMore(members.length === pageSize);

                dispatch(setMembers({ channelId, members, total: totalMembers, page, totalMembers }));
            } catch (err) {
                dispatch(setError({ channelId, error: err }));
            } finally {
                dispatch(setLoading({ channelId, loading: false }));
            }
        },
        [dispatch, channelId, pageSize, total, allMembers.length],
    );

    useEffect(() => {
        fetchMembers(searchText, 1, true);
        setVisibleCount(pageSize);
        setCurrentPage(1);
    }, [channelId, searchText, pageSize]);

    const reset = useCallback(() => {
        clearTimeout(debounceTimer.current);
        setVisibleCount(pageSize);
        setCurrentPage(1);
        fetchMembers(searchText, 1, true);
    }, [dispatch, channelId, pageSize]);

    const updateSearchText = useCallback(
        text => {
            clearTimeout(debounceTimer.current);
            debounceTimer.current = setTimeout(() => {
                setSearchText(text);
                if (text === '') {
                    reset();
                }
            }, debounceMs);
        },
        [debounceMs, reset],
    );

    let visibleMembers;
    if (membersCount) {
        visibleMembers = allMembers.filter(m => m.status !== 0).slice(0, membersCount);
    } else {
        visibleMembers = allMembers.filter(m => m.status !== 0).slice(0, visibleCount);
    }

    const loadMore = useCallback(() => {
        if (!membersCount && visibleCount < total && !loading) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchMembers(searchText, nextPage);
            setVisibleCount(c => c + pageSize);
        }
    }, [membersCount, visibleCount, total, currentPage, pageSize, fetchMembers, searchText, loading]);

    return {
        members: visibleMembers,
        loading,
        error,
        searchText,
        updateSearchText,
        reset,
        loadMore,
        hasMore: canFetchMore,
        totalRecords: total,
    };
}
